import { Link, useNavigate } from "react-router";
import type { PublicStory } from "@storyai/shared";
import {
  EmptyState,
  ErrorPanel,
  Loading,
  MutationError,
  PageHeader,
  VisibilityBadge,
  formatDate,
} from "../components/ui";
import { useCreateStory, useMyStories, useVersions } from "../lib/queries";
import { versionStatusLabel } from "../lib/versions";

export function MyStoriesPage() {
  const q = useMyStories();
  const create = useCreateStory();
  const navigate = useNavigate();

  async function onCreate() {
    const { story } = await create.mutateAsync({});
    navigate(`/stories/${story.id}/edit`);
  }

  const createButton = (
    <button type="button" className="btn btn-primary" onClick={onCreate} disabled={create.isPending}>
      {create.isPending ? "Creating…" : "Create story"}
    </button>
  );

  // Version stories are reached through their base, never listed on their own.
  const bases = (q.data?.stories ?? []).filter((s) => !s.versionOf);

  return (
    <div className="page">
      <PageHeader title="My Stories" subtitle="Stories you've written." actions={createButton} />
      <MutationError error={create.error} />
      {q.isPending && <Loading />}
      {q.isError && <ErrorPanel error={q.error} />}
      {q.data &&
        (bases.length === 0 ? (
          <EmptyState title="You haven't written a story yet" action={createButton}>
            A story is a branching adventure run by an AI game master. You write the chapters,
            the scenes inside them, the hidden notes only the AI can see, and the conditions for
            moving forward. Players read each chapter, then talk to the AI to explore, find
            things, and push the story on.
          </EmptyState>
        ) : (
          <ul className="story-list">
            {bases.map((s) => (
              <StoryRow key={s.id} story={s} />
            ))}
          </ul>
        ))}
    </div>
  );
}

function StoryRow({ story: s }: { story: PublicStory }) {
  return (
    <>
      <li className="story-row">
        <Link to={`/stories/${s.id}`} className="story-row-main">
          {s.coverUrl ? (
            <img className="thumb" src={s.coverUrl} alt="" />
          ) : (
            <div className="thumb thumb-placeholder" aria-hidden="true">
              {s.title.slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="story-row-text">
            <div className="story-row-title">{s.title}</div>
            <div className="muted small">
              {s.chapterCount} {s.chapterCount === 1 ? "chapter" : "chapters"} · updated{" "}
              {formatDate(s.updatedAt)}
            </div>
          </div>
        </Link>
        <div className="story-row-side">
          <VisibilityBadge visibility={s.visibility} />
          <Link to={`/stories/${s.id}/edit`} className="btn btn-sm">
            Edit
          </Link>
        </div>
      </li>
      <VersionRows baseId={s.id} />
    </>
  );
}

/** Versions nested under their base. Each base fetches its own list. */
function VersionRows({ baseId }: { baseId: string }) {
  const q = useVersions(baseId);
  const versions = q.data?.versions ?? [];
  if (versions.length === 0) return null;
  return (
    <>
      {versions.map((v) => (
        <li key={v.id} className="story-row story-row-version">
          <Link to={`/stories/${v.storyId}`} className="story-row-main">
            <div className="version-indent" aria-hidden="true">
              ↳
            </div>
            <div className="story-row-text">
              <div className="story-row-title">
                {v.title}
                {v.language && <span className="tag">{v.language}</span>}
              </div>
              <div className="muted small">Version · created {formatDate(v.createdAt)}</div>
            </div>
          </Link>
          <div className="story-row-side">
            <span className={`badge badge-version-${v.status}${v.enabled ? "" : " badge-muted"}`}>
              {versionStatusLabel(v)}
            </span>
            <Link to={`/stories/${v.storyId}/map`} className="btn btn-sm">
              Map
            </Link>
          </div>
        </li>
      ))}
    </>
  );
}
